// ⚠ `#1084` — EN TOUT PREMIER : un onglet reste ouvert pendant un deploiement,
//   la premiere route paresseuse demandee ensuite reclame un fichier disparu
//   (« Failed to fetch dynamically imported module … »). La garde recharge la
//   page UNE fois. ⚠ Posee ici, elle s'installe des l'evaluation du point
//   d'entree — donc AVANT le premier rendu et avant toute route paresseuse.
//   (Les `import` etant hisses, ce n'est pas « avant les imports » : c'est
//   avant tout ce que l'application FAIT.)
import { installerGardeModuleManquant } from './lib/rechargerSurModuleManquant';
installerGardeModuleManquant();

import './lib/i18n';  // `#796` init i18n (depuis le catalogue partage)
import { appliquerLangueDuCompte } from './lib/langueBootstrap';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { useThemeStore, appliquerTheme } from './stores/themeStore';
import './styles/globals.css';

/** QueryClient global — staleTime 5 min par défaut (données Orbicore peu volatiles) */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Point d'entrée — initialise le thème AVANT le premier render pour éviter le flash.
 * V2.1 (BULLE V2.1) : default 'light' (CR.1) + sélecteurs dual `.dark`/`.light` class
 * + `data-theme` attribute (G2 mode dual tailwind). Cf. themeStore.ts pour détails.
 * DECISIONS #079 / #088 superseded par CR.1 V2 (acté Hkm 05/05/2026).
 */

/**
 * ⚠ PURGE D UNE CLE ORPHELINE — ELLE N A PLUS D ECRAN POUR LA RETIRER.
 *   `sfmt_welcome_photos_v1` portait DEUX images en base64, posees par la
 *   section « Welcome Page — Photos produits » des Parametres. Cette section a
 *   ete coupee le 18/08/2026 : son hook `useWelcomePhotos` n avait qu UN
 *   appelant — l ecran qui posait les photos — et AUCUN lecteur. Couper l ecran
 *   sans purger la cle aurait laisse ces images dans le `localStorage` de chaque
 *   poste, sans plus aucun bouton pour s en debarrasser.
 * ⚠ AU CHARGEMENT, PAS DANS UN COMPOSANT : la purge doit avoir lieu meme si
 *   personne ne rouvre jamais la page Parametres — c est justement le cas ou la
 *   cle resterait pour toujours.
 * ⚠ `try` PARCE QUE LE `localStorage` PEUT ETRE REFUSE (navigation privee,
 *   politique de site) : un boot qui echoue sur un nettoyage serait pire que la
 *   cle qu il nettoie.
 */
try {
  localStorage.removeItem('sfmt_welcome_photos_v1');
} catch { /* stockage indisponible — rien a nettoyer */ }

// `#1060` — LE THÈME N'EST PLUS FORCÉ : il vient du store persisté (défaut
// sombre), déjà appliqué par l'anti-flash de `index.html` AVANT React. On
// ré-applique la valeur réhydratée pour garantir l'accord DOM ↔ store même si
// l'anti-flash n'a pas tourné (le bouton du menu utilisateur bascule ensuite).
appliquerTheme(useThemeStore.getState().theme);

/**
 * `#814` — LA LANGUE DU COMPTE, POSÉE AVANT LE PREMIER RENDU — EXACTEMENT COMME
 *   LE THÈME CI-DESSUS, ET POUR LA MÊME RAISON.
 *
 * ⚠ Elle est DÉJÀ là : `userStore` (persist zustand) réhydrate `User.language`
 *   synchronement. La poser ici supprime le flash FR→EN au chargement ; sans
 *   elle, `/auth/me` l'appliquait depuis un `useEffect` d'AppShell — après le
 *   premier paint. `/auth/me` reste la vérité et re-pilote juste après.
 */
appliquerLangueDuCompte();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('[Orbicore] #root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);
